/**
 * Stock Actions Modal Component
 * 
 * State machine-based modal for managing stock actions for a specific warehouse/location.
 * 
 * Architecture:
 * - Single modal instance with multiple "screens" (menu, stock_entry_quick_action, etc.)
 * - Config-driven actions via STOCK_ACTIONS registry
 * - Modal tracking key changes per screen for state tracking
 * - Preserves existing open/close behavior, resets to menu on close
 * 
 * Features:
 * - Stock Entry Quick Actions (Material Receipt, Issue, Transfer, Manufacturing, Repack)
 * - Stock Reconciliation actions (Opening Stock, Stock Reconciliation)
 * - Apple-style design matching Gallery Actions modal
 */

import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";
import { STOCK_ACTIONS, type StockActionConfig, type StockActionId } from "./stockActionsConfig";
import { MiniStockEntryQuickAction } from "./MiniStockEntryQuickAction";
import { MiniStockReconciliationQuickAction } from "./MiniStockReconciliationQuickAction";
import { getModalTrackingKey } from "./MiniStockEntryQuickAction";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

type ModalScreen = "menu" | "stock_entry_quick_action" | "opening_stock" | "stock_reconciliation";

interface StockActionsModalState {
  screen: ModalScreen;
  activeActionId?: StockActionId;
}

interface StockActionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouseName?: string; // Optional - if not provided, warehouses won't be locked
  itemCode: string;
  disabled?: boolean;
  lockWarehouses?: boolean; // If false, allow full warehouse selection (no locked warehouses)
}

export function StockActionsModal({
  open,
  onOpenChange,
  warehouseName,
  itemCode,
  disabled = false,
  lockWarehouses = true, // Default to true for backward compatibility
}: StockActionsModalProps) {
  const { language } = useLanguage();
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      stockActions: t("stock_actions", "Stock Actions"),
      createStockEntry: t("create_stock_entry", "Create Stock Entry"),
      openingStock: t("opening_stock", "Opening Stock"),
      stockReconciliation: t("stock_reconciliation", "Stock Reconciliation"),
      manageStockActions: t("manage_stock_actions", "Manage stock actions"),
      manageStockForTemplate: t("manage_stock_for", "Manage stock for {warehouse}"),
    };
  }, [language]);

  const tActionLabel = useMemo(() => {
    return (action: StockActionConfig) =>
      (action.labelKey ? getUITranslation(action.labelKey, language) : null) || action.label;
  }, [language]);

  const tActionDescription = useMemo(() => {
    return (action: StockActionConfig) =>
      (action.descriptionKey ? getUITranslation(action.descriptionKey, language) : null) || action.description;
  }, [language]);

  // Modal state machine
  const [state, setState] = useState<StockActionsModalState>({ screen: "menu" });

  // Reset to menu when modal closes
  useEffect(() => {
    if (!open) {
      setState({ screen: "menu" });
    }
  }, [open]);

  // Get active action config
  const activeAction = useMemo(
    () => (state.activeActionId ? STOCK_ACTIONS.find(a => a.id === state.activeActionId) : null),
    [state.activeActionId]
  );

  // Dynamic modal title based on screen
  const modalTitle = useMemo(() => {
    if (state.screen === "menu") return ui.stockActions;
    if (state.screen === "stock_entry_quick_action") return (activeAction ? tActionLabel(activeAction) : ui.createStockEntry);
    if (state.screen === "opening_stock") return ui.openingStock;
    if (state.screen === "stock_reconciliation") return ui.stockReconciliation;
    return ui.stockActions;
  }, [state.screen, activeAction, ui, tActionLabel]);

  // Modal tracking key for analytics/state tracking
  const trackingKey = useMemo(
    () => getModalTrackingKey(state.screen, state.activeActionId),
    [state.screen, state.activeActionId]
  );

  // Handle action selection
  const handleActionClick = (action: StockActionConfig) => {
    if (disabled) return;

    if (action.kind === "stock_entry_quick_action") {
      setState({ screen: "stock_entry_quick_action", activeActionId: action.id });
      return;
    }

    if (action.kind === "opening_stock") {
      setState({ screen: "opening_stock", activeActionId: action.id });
      return;
    }

    if (action.kind === "stock_reconciliation") {
      setState({ screen: "stock_reconciliation", activeActionId: action.id });
      return;
    }
  };

  // Handle back navigation
  const handleBack = () => {
    setState({ screen: "menu" });
  };

  // Group actions by kind for UI organization
  const stockEntryActions = useMemo(
    () => STOCK_ACTIONS.filter(a => a.kind === "stock_entry_quick_action"),
    []
  );

  const reconciliationActions = useMemo(
    () => STOCK_ACTIONS.filter(a => a.kind === "opening_stock" || a.kind === "stock_reconciliation"),
    []
  );

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      // Reset state when modal closes
      if (!open) {
        setState({ screen: "menu" });
      }
    }}>
      <DialogContent className="max-w-[680px] max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden">
        {/* Top fade-out gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        
        <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
          <DialogHeader className="px-10 pt-20 pb-4 text-center">
            {/* Back button - only show on sub-screens */}
            {state.screen !== "menu" && (
              <div className="absolute top-6 left-6">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="h-10 w-10 rounded-full"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </div>
            )}

            <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
              {modalTitle}
            </DialogTitle>
            <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
              {state.screen === "menu" 
                ? (warehouseName
                    ? ui.manageStockForTemplate.replace("{warehouse}", String(warehouseName))
                    : ui.manageStockActions)
                : (activeAction ? tActionDescription(activeAction) : "")}
            </DialogDescription>

            {/* Tracking key for analytics/state tracking */}
            <div data-modal-key={trackingKey} className="hidden" />
          </DialogHeader>
          
          <div className={cn(
            "pb-10 px-10",
            (state.screen === "stock_entry_quick_action" || state.screen === "opening_stock" || state.screen === "stock_reconciliation") ? "pt-0 p-0 flex flex-col flex-1 min-h-0" : "pt-4"
          )}>
            {/* Menu Screen */}
            {state.screen === "menu" && (
            <div className="space-y-6">
              {/* Stock Entry Types Group */}
              <div className="bg-gray-50 dark:bg-gray-950 rounded-lg overflow-hidden border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
                  {stockEntryActions.map((action, index) => {
                    const IconComponent = action.icon;
                    const isLast = index === stockEntryActions.length - 1;
                    
                    return (
                <button
                        key={action.id}
                  type="button"
                        onClick={() => handleActionClick(action)}
                  disabled={disabled}
                  className={cn(
                    "w-full flex items-center gap-5 min-h-[60px] px-6 py-4",
                    "hover:bg-gray-100/80 dark:hover:bg-gray-900/60 transition-all duration-200 ease-out",
                    "active:bg-gray-100 dark:active:bg-gray-900/70",
                    "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:bg-transparent",
                          !isLast && "border-b border-gray-200/50 dark:border-gray-700/50"
                  )}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                            {tActionLabel(action)}
                    </div>
                    <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                            {tActionDescription(action)}
                    </div>
                  </div>
                </button>
                    );
                  })}
                </div>

                {/* Stock Reconciliation Group */}
                <div className="bg-gray-50 dark:bg-gray-950 rounded-lg overflow-hidden border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
                  {reconciliationActions.map((action, index) => {
                    const IconComponent = action.icon;
                    const isLast = index === reconciliationActions.length - 1;
                    
                    return (
                <button
                        key={action.id}
                  type="button"
                        onClick={() => handleActionClick(action)}
                  disabled={disabled}
                  className={cn(
                    "w-full flex items-center gap-5 min-h-[60px] px-6 py-4",
                    "hover:bg-gray-100/80 dark:hover:bg-gray-900/60 transition-all duration-200 ease-out",
                    "active:bg-gray-100 dark:active:bg-gray-900/70",
                    "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:bg-transparent",
                          !isLast && "border-b border-gray-200/50 dark:border-gray-700/50"
                  )}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                            {tActionLabel(action)}
                    </div>
                    <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                            {tActionDescription(action)}
                    </div>
                  </div>
                </button>
                    );
                  })}
                    </div>
                  </div>
            )}

            {/* Stock Entry Quick Action Screen */}
            {state.screen === "stock_entry_quick_action" && activeAction?.stockEntryType && (
              <MiniStockEntryQuickAction
                itemCode={itemCode}
                warehouseContext={warehouseName || ""}
                stockEntryTypeLocked={activeAction.stockEntryType}
                warehouseMode={activeAction.warehouseMode ?? "to"}
                valueDisplay={activeAction.valueDisplay ?? "incoming"}
                onDone={() => onOpenChange(false)}
                lockWarehouses={lockWarehouses}
              />
            )}

            {/* Opening Stock Screen */}
            {state.screen === "opening_stock" && (
              <MiniStockReconciliationQuickAction
                itemCode={itemCode}
                warehouseContext={warehouseName || ""}
                purposeLocked="Opening Stock"
                onDone={() => onOpenChange(false)}
                lockWarehouses={lockWarehouses}
              />
            )}

            {/* Stock Reconciliation Screen */}
            {state.screen === "stock_reconciliation" && (
              <MiniStockReconciliationQuickAction
                itemCode={itemCode}
                warehouseContext={warehouseName || ""}
                purposeLocked="Stock Reconciliation"
                onDone={() => onOpenChange(false)}
                lockWarehouses={lockWarehouses}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

